// Importing necessary fonts from Google Fonts via 'next/font/google'
import {
    Inter,
    Noto_Sans,
    Noto_Serif,
    Ubuntu,
    Roboto,
    Great_Vibes,
    Kalam,
} from 'next/font/google';

// Importing shared types from 'globalTypes'
import { FeatureDescriptions, Months, Rashi, Testimonial } from './globalTypes';

// Configuring revalidation for incremental static regeneration
export const revalidate = { revalidate: 3600 };

// Configurations for various Google Fonts
export const inter = Inter({
    // Used in one place only: not preloaded on every page.
    preload: false,
    subsets: ['latin'],
    variable: '--font-inter',
});

export const noto_sans = Noto_Sans({
    display: 'swap',
    style: 'normal',
    variable: '--font-sans',
    subsets: ['latin'],
});

export const noto_serif = Noto_Serif({
    display: 'swap',
    style: 'normal',
    variable: '--font-serif',
    subsets: ['latin'],
});

export const ubuntu = Ubuntu({
    display: 'swap',
    style: 'normal',
    variable: '--font-ubuntu',
    subsets: ['latin'],
    weight: ['400', '500', '700'],
});

export const kalam = Kalam({
    // Used in one place only: not preloaded on every page.
    preload: false,
    display: 'swap',
    style: 'normal',
    variable: '--font-kalam',
    subsets: ['latin'],
    weight: ['300', '400', '700'],
});

export const roboto = Roboto({
    // Used in one place only: not preloaded on every page.
    preload: false,
    display: 'swap',
    style: 'normal',
    variable: '--font-robot',
    subsets: ['latin'],
    weight: '400',
});

export const great_vibes = Great_Vibes({
    // Used in one place only: not preloaded on every page.
    preload: false,
    display: 'swap',
    style: 'normal',
    subsets: ['latin'],
    variable: '--font-good-vibes',
    weight: '400',
});

// Descriptions of features displayed on the demo page
export const DEMO_DESCRIPTIONS: FeatureDescriptions[] = [
    {
        icon: `/indian-flag.svg`,
        heading: 'Truly Indian - Available in 5 Indian Languages',
        descriptions: [
            'Dharmayana is available in Hindi, Kannada, Marathi, Telugu and Tamil',
            'Coming soon to Gujarati, Bengali, Assamese and more languages',
        ],
        alt: 'Calendar Icon',
        carouselImage: '/Carousel1.jpg'
    },
    {
        icon: `${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/CalendarCheck.svg`,
        heading: 'Daily Calendar',
        descriptions: [
            'Stay informed about important dates with our easy-to-use Hindu calendar',
            'Find auspicious & inauspicious times during the day',
            'Set reminders for good and bad muhurta',
        ],
        alt: 'Sun Set Icon',
        carouselImage: '/Carousel2.jpg'
    },
    {
        icon: `${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/SunHorizon.svg`,
        heading: 'Panchanga',
        descriptions: [
            'Find Tithi, Vaara, Nakshantra,Yoga and Karana',
            'View Panchanga for past and future dates',
        ],
        alt: 'Hourglass Icon',
        carouselImage: '/Carousel3.jpg'
    },
    {
        icon: `${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/HourglassMedium.svg`,
        heading: 'Shubh Muhurta',
        descriptions: [
            'Align your actions with favourable celestial movements ',
            'Find auspicious days for starting new projects, ceremonies, and special occasions',
        ],
        alt: 'Book Icon',
        carouselImage: '/Carousel4.jpg'
    },
    {
        icon: `${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/lamp.svg `,
        heading: 'Festivals and Observances',
        descriptions: [
            'Find dates for festivals and observances like Amavasya, Ekadasi, and more',
            'Get timely reminders to prepare for special days',
            'Learn about the significance of each special day',
        ],
        alt: 'Lamp Icon',
        carouselImage: '/Carousel5.jpg'
    },
    {
        icon: `${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/PrarthnaIcon.svg`,
        heading: 'Prarthana',
        descriptions: [
            'Find prayers for your ista-devata - along with their meanings.',
            'Daily 5-minute prayer is now very easy with a customized daily prayer list.',
        ],
        alt: 'Prarthana Icon',
        carouselImage: '/Carousel6.jpg'
    },
];

// List of testimonials for the app
export const TESTIMONIALS: Testimonial[] = [
    {
    review: 'I start my day with Panchanga and Prarthana here. Simple and reliable.',
    author: 'Ananya Rao',
    location: 'Bengaluru',
    stars: 5
  },
  {
    review: 'Clear Rashifal, clean UI, and prayers I can follow daily.',
    author: 'Rajesh Mehta',
    location: 'Ahmedabad',
    stars: 5
  },
  {
    review: 'Booked a temple pooja - video arrived the same day.',
    author: 'Priya Subramanian',
    location: 'Singapore',
    stars: 5
  },
  {
    review: 'Feels rooted and modern at once. I use it every morning.',
    author: 'Arun Sharma',
    location: 'New Jersey',
    stars: 5
  },
  {
    review: 'Love the daily guidance. The mantras are easy to follow and help me stay centred.',
    author: 'Sonal Deshpande',
    location: 'Pune',
    stars: 5
  },
  {
    review: 'The app has become part of my morning ritual. Accurate Panchanga and peaceful chants.',
    author: 'Harish Menon',
    location: 'Kochi',
    stars: 5
  },
  {
    review: 'Booked a pooja for my parents. Smooth process, authentic experience, and timely updates.',
    author: 'Meera Kulkarni',
    location: 'Sydney',
    stars: 5
  },
  {
    review: 'Beautiful design, trustworthy content. My go-to place for Rashifal and prayers.',
    author: 'Vikram Sethi',
    location: 'Delhi',
    stars: 5
  }
];

// Screenshots of the app for showcasing
export const APP_SCREEN_SHOTS: string[] = [
    "App's Home page demo screenshot",
    "App's Panchanga page demo screenshot",
    "App's 'Good Day For' search feature screenshot",
    "App's Festival page demo screenshot",
    "App's Home demo screenshot",
    "App's Home demo screenshot",
];

// Days of the week mapping
export const daysOfWeek: { [day: string]: string } = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
};

// Empty months data structure for festivals
export const MONTHS: Months = {
    JANUARY: [],
    FEBRUARY: [],
    MARCH: [],
    APRIL: [],
    MAY: [],
    JUNE: [],
    JULY: [],
    AUGUST: [],
    SEPTEMBER: [],
    OCTOBER: [],
    NOVEMBER: [],
    DECEMBER: [],
};

// Default location settings
export const DEFAULT_LOCATION = {
    lat: 23.41667,
    long: 75.5,
    city: 'Ujjain',
};

// Rashis (Zodiac signs) with their respective images and banners
export const RASHIES: Rashi[] = [
    {
        vedic_name: 'Mesha',
        english_name: 'Aries',
        image_url: `/rashies-new/mesha.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/aries.png`
    },
    {
        vedic_name: 'Vrishabha',
        english_name: 'Taurus',
        image_url: `/rashies-new/vrishabha.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/taurus.png`

    },
    {
        vedic_name: 'Mithuna',
        english_name: 'Gemini',
        image_url: `/rashies-new/mithuna.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/gemini.png`

    },
    {
        vedic_name: 'Karka',
        english_name: 'Cancer',
        image_url: `/rashies-new/karka.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/cancer.png`
    },
    {
        vedic_name: 'Simha',
        english_name: 'Leo',
        image_url: `/rashies-new/simha.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/leo.png`
    },
    {
        vedic_name: 'Kanya',
        english_name: 'Virgo',
        image_url: `/rashies-new/kanya.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/virgo.png`,
    },
    {
        vedic_name: 'Tula',
        english_name: 'Libra',
        image_url: `/rashies-new/tula.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/libra.png`,
    },
    {
        vedic_name: 'Vrischika',
        english_name: 'Scorpio',
        image_url: `/rashies-new/vrischika.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/scorpio.png`,
    },
    {
        vedic_name: 'Dhanu',
        english_name: 'Sagittarius',
        image_url: `/rashies-new/dhanu.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/sagittarius.png`,
    },
    {
        vedic_name: 'Makara',
        english_name: 'Capricorn',
        image_url: `/rashies-new/makara.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/capricorn.png`,
    },
    {
        vedic_name: 'Kumbha',
        english_name: 'Aquarius',
        image_url: `/rashies-new/kumbha.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/aquarius.png`,
    },
    {
        vedic_name: 'Meena',
        english_name: 'Pisces',
        image_url: `/rashies-new/meena.webp`,
        banner_url:`https://d161fa2zahtt3z.cloudfront.net/rashi/large/pisces.png`,
    },
]

// Types of Prarthana collections for categorization
export const TYPES_OF_PRARTHANA_COLLECTION = [
    {
        type: 'deities',
        by_line: 'Find Prarthana for Devta',
        banner_url: '/prarthana-by-devta-banner.png',
        redirection_url: '/deities'
    },
    {
        type: 'festivals',
        by_line: 'Find Prarthana for Festival',
        banner_url: '/prarthana-by-festival-banner.png',
        redirection_url: '/prarthana/festival'
    },
    {
        type: 'purposes',
        by_line: 'Find Prarthana for Purpose',
        banner_url: '/prarthana-by-purpose-banner.png',
        redirection_url: '/prarthana/purpose'

    }
]

// OG image is a static branding asset served from the production host in all
// environments — a stage host here leaks into prod metadata (see SEO T1.1).
export const OPEN_GRAPH_IMAGE_PATH = 'https://dharmayana.in/opengraph.jpg';

// Origin of the host actually serving the page. Page-level schema URLs
// (breadcrumb items, mainEntityOfPage, article @id) must agree with it, or
// staging emits markup that cannot be validated against the page it came from.
// Entity @ids such as the Organization stay on the production host in every
// environment: they identify the organisation, not a servable URL.
export const SITE_ORIGIN =
    process.env.NEXT_PUBLIC_ENV === 'production' ? 'https://dharmayana.in' : 'https://web.stage.dharmayana.in';

// The site-wide fallback OG image, declared once at its real size: 1200x630,
// the size link previews use (it was a 4.3 MB 3600x1890 file, too big for
// WhatsApp previews; now a 244 KB JPEG at quality 95).
export const OPEN_GRAPH_IMAGE_LIST = [
    {
        url: OPEN_GRAPH_IMAGE_PATH,
        width: 1200,
        height: 630,
        alt: 'Dharmayana Banner',
    },
];

// One image, declared at the size it actually is. The previous version repeated
// the same URL six times under six different dimensions, five of which were
// false; crawlers take the first entry and the rest were noise.
export const fetchOpenGrapImageList = (image_url: string, width?: number, height?: number) => {
    return [width && height ? { url: image_url, width, height } : { url: image_url }];
}